/**
 * /admin/releases — manage the one-button release pipeline.
 *
 * What you can do here:
 *   - Draft a release (title, banner summary, CTA, audience, optional
 *     linked feature flag, optional richer markdown email body).
 *   - Edit drafts before they go out.
 *   - Hit "Publish" — confirms the audience size + side-effects, then
 *     atomically: flips the linked flag, emails the audience, lights up
 *     the homepage banner.
 *   - Archive published releases when the moment has passed (banner
 *     turns off, history is kept).
 *
 * Common use cases:
 *   1. Site goes live (flag: public_signups_enabled, audience: waitlist)
 *   2. Titanium goes live (flag: titanium_live, audience: paying_users)
 *   3. New improvement shipped (no flag, audience: all_users)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Send, Archive, Trash2, Pencil, Megaphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import PageTitle from '../components/PageTitle';
import SpellCheckDialog from '../components/SpellCheckDialog';
import { useSpellCheckOnSave } from '../hooks/useSpellCheckOnSave';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrAbove } from '../lib/tier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  draft: { label: 'Draft', cls: 'bg-[#FEF3C7] text-[#92400E]' },
  published: { label: 'Live', cls: 'bg-[#DCFCE7] text-[#166534]' },
  archived: { label: 'Archived', cls: 'bg-[#F1F5F9] text-[#475569]' },
};

const AUDIENCE_OPTIONS = [
  { value: 'all_users', label: 'Every Forge Ledger user' },
  { value: 'waitlist', label: 'Everyone on the launch waitlist' },
  { value: 'paying_users', label: 'Paying users (Steel + Damascus + Titanium)' },
  { value: 'tier:pro', label: 'Steel tier users only' },
  { value: 'tier:pro_plus', label: 'Damascus tier users only' },
  { value: 'tier:pro_premium', label: 'Titanium tier users only' },
];

const FLAG_OPTIONS = [
  { value: '', label: 'No flag — banner + email only' },
  { value: 'public_signups_enabled', label: 'Open public signups (site launch)' },
  { value: 'titanium_live', label: 'Open Titanium tier (Titanium launch)' },
];

const EMPTY_FORM = {
  title: '',
  summary: '',
  body_md: '',
  cta_label: '',
  cta_url: '',
  audience: 'all_users',
  linked_flag: '',
  email_subject: '',
};

const AdminReleases = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // { release | 'new' }
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(null);  // release_id
  const [confirmPublish, setConfirmPublish] = useState(null);  // release object

  useEffect(() => {
    if (user && !isAdminOrAbove(user)) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/releases`, { withCredentials: true });
      setReleases(r.data || []);
    } catch (err) {
      toast.error('Could not load releases.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && isAdminOrAbove(user)) load();
  }, [user, load]);

  const openNew = () => {
    setEditing('new');
    setForm(EMPTY_FORM);
  };

  const openEdit = (rel) => {
    setEditing(rel);
    setForm({
      title: rel.title || '',
      summary: rel.summary || '',
      body_md: rel.body_md || '',
      cta_label: rel.cta_label || '',
      cta_url: rel.cta_url || '',
      audience: rel.audience || 'all_users',
      linked_flag: rel.linked_flag || '',
      email_subject: rel.email_subject || '',
    });
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const buildPayload = useCallback(() => ({
    ...form,
    linked_flag: form.linked_flag || null,
    cta_label: form.cta_label || null,
    cta_url: form.cta_url || null,
    email_subject: form.email_subject || null,
  }), [form]);

  const persist = useCallback(async (payload) => {
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/releases`, payload, { withCredentials: true });
        toast.success('Draft saved.');
      } else {
        await axios.put(`${API}/admin/releases/${editing.release_id}`, payload, { withCredentials: true });
        toast.success('Draft updated.');
      }
      closeEditor();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save draft.');
    } finally {
      setSaving(false);
    }
  }, [editing, load]);

  const sc = useSpellCheckOnSave({ buildPayload, persist });

  const save = () => {
    if (!form.title.trim() || !form.summary.trim()) {
      toast.error('Title and summary are required.');
      return;
    }
    sc.save();
  };

  const doPublish = async (rel) => {
    setPublishing(rel.release_id);
    setConfirmPublish(null);
    try {
      const r = await axios.post(`${API}/admin/releases/${rel.release_id}/publish`, {}, { withCredentials: true });
      const s = r.data?.publish_summary || {};
      toast.success(
        `Published. Sent to ${s.emails_sent || 0} recipient${s.emails_sent === 1 ? '' : 's'}` +
        (s.emails_failed ? ` (${s.emails_failed} failed)` : '') +
        (s.flag_flipped ? ` · Flag ${s.flag_flipped} flipped ON` : '')
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publish failed.');
    } finally {
      setPublishing(null);
    }
  };

  const archive = async (rel) => {
    if (!window.confirm(`Archive "${rel.title}"? The banner will stop showing but the email history stays.`)) return;
    try {
      await axios.post(`${API}/admin/releases/${rel.release_id}/archive`, {}, { withCredentials: true });
      toast.success('Archived.');
      load();
    } catch (err) {
      toast.error('Could not archive.');
    }
  };

  const remove = async (rel) => {
    if (!window.confirm(`Delete draft "${rel.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/releases/${rel.release_id}`, { withCredentials: true });
      toast.success('Draft deleted.');
      load();
    } catch (err) {
      toast.error('Could not delete.');
    }
  };

  const grouped = useMemo(() => {
    const drafts = releases.filter((r) => r.status === 'draft');
    const live = releases.filter((r) => r.status === 'published');
    const archived = releases.filter((r) => r.status === 'archived');
    return { drafts, live, archived };
  }, [releases]);

  if (!user || !isAdminOrAbove(user)) return null;

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="admin-releases-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0052FF] mb-6" data-testid="admin-releases-back">
        <ArrowLeft className="w-4 h-4" />Back to dashboard
      </Link>

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <PageTitle
          title="Releases"
          subtitle="One-button launches. Flip the linked flag, email the audience, light up the homepage banner — all at once."
          className="mb-0"
        />
        <Button onClick={openNew} className="bg-[#0052FF] hover:bg-[#003ECC] text-white" data-testid="releases-new-button">
          <Plus className="w-4 h-4 mr-2" />Draft a release
        </Button>
      </div>

      {loading ? (
        <p className="text-[#64748B]">Loading…</p>
      ) : (
        <div className="space-y-8">
          <Section title="Drafts" rows={grouped.drafts} onEdit={openEdit} onDelete={remove} onPublish={(r) => setConfirmPublish(r)} publishing={publishing} testId="releases-section-drafts" />
          <Section title="Live" rows={grouped.live} onArchive={archive} testId="releases-section-live" />
          <Section title="Archived" rows={grouped.archived} testId="releases-section-archived" />
        </div>
      )}

      {/* Draft editor */}
      <Dialog open={editing !== null} onOpenChange={(open) => { if (!open) closeEditor(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Draft a release' : 'Edit draft'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Field label="Title" hint="Used in the banner heading and the email subject when none is given.">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. The doors are open" data-testid="releases-form-title" />
            </Field>
            <Field label="Summary" hint="One short sentence — banner + email lead-in.">
              <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="e.g. Forge Ledger is live. Take the free tour." data-testid="releases-form-summary" />
            </Field>
            <Field label="Body (optional, markdown)" hint="Richer copy for the email. Paragraphs separated by blank lines.">
              <Textarea value={form.body_md} onChange={(e) => setForm({ ...form, body_md: e.target.value })} rows={5} placeholder="Tell them what changed, why it matters, and what to try first." data-testid="releases-form-body" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="CTA label" hint="Button text in banner + email.">
                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="e.g. Try it now" data-testid="releases-form-cta-label" />
              </Field>
              <Field label="CTA URL" hint="Where the button goes.">
                <Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="e.g. /pricing" data-testid="releases-form-cta-url" />
              </Field>
            </div>
            <Field label="Audience" hint="Who gets the email (banner targets the same group).">
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="releases-form-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Linked feature flag" hint="Optional — auto-flip this flag ON when you publish.">
              <Select value={form.linked_flag || '__none__'} onValueChange={(v) => setForm({ ...form, linked_flag: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="releases-form-flag"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No flag — banner + email only</SelectItem>
                  {FLAG_OPTIONS.filter((o) => o.value).map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Email subject (optional)" hint="Defaults to the release title if blank.">
              <Input value={form.email_subject} onChange={(e) => setForm({ ...form, email_subject: e.target.value })} placeholder="Same as title" data-testid="releases-form-email-subject" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={closeEditor} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#0052FF] hover:bg-[#003ECC] text-white" data-testid="releases-form-save">
                {saving ? 'Saving…' : 'Save draft'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish confirmation */}
      <Dialog open={confirmPublish !== null} onOpenChange={(open) => { if (!open) setConfirmPublish(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Publish this release?</DialogTitle>
          </DialogHeader>
          {confirmPublish && (
            <div className="text-sm text-[#475569] space-y-3 pt-2">
              <div className="rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] p-3">
                <p className="font-semibold text-[#020617] mb-1">&ldquo;{confirmPublish.title}&rdquo;</p>
                <p className="text-xs text-[#64748B]">{confirmPublish.summary}</p>
              </div>
              <p className="font-semibold text-[#020617]">When you click Publish, all of these will happen at once:</p>
              <ul className="space-y-1.5 pl-5 list-disc">
                <li>Email goes out to <strong>{confirmPublish.audience_label || confirmPublish.audience}</strong>.</li>
                {confirmPublish.linked_flag && (
                  <li>Feature flag <code className="bg-[#F1F5F9] px-1 py-0.5 rounded text-xs">{confirmPublish.linked_flag}</code> flips <strong>ON</strong>.</li>
                )}
                <li>Homepage banner lights up for everyone in the audience.</li>
              </ul>
              <p className="text-xs text-[#94A3B8] pt-1">Drafts can be edited freely; once published, the release is final (you can archive it later to turn the banner off).</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setConfirmPublish(null)}>Cancel</Button>
            <Button
              onClick={() => doPublish(confirmPublish)}
              disabled={publishing !== null}
              className="bg-[#0052FF] hover:bg-[#003ECC] text-white"
              data-testid="releases-confirm-publish"
            >
              <Send className="w-4 h-4 mr-2" />{publishing ? 'Publishing…' : 'Publish now'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <SpellCheckDialog {...sc.dialogProps} />
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-1.5 block">{label}</Label>
    {children}
    {hint && <p className="text-xs text-[#94A3B8] mt-1">{hint}</p>}
  </div>
);

const Section = ({ title, rows, onEdit, onDelete, onPublish, onArchive, publishing, testId }) => {
  if (!rows.length) return null;
  return (
    <section data-testid={testId}>
      <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-[#64748B] mb-3">{title} · {rows.length}</h2>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <ReleaseRow key={r.release_id} release={r} onEdit={onEdit} onDelete={onDelete} onPublish={onPublish} onArchive={onArchive} publishing={publishing} />
        ))}
      </div>
    </section>
  );
};

const ReleaseRow = ({ release, onEdit, onDelete, onPublish, onArchive, publishing }) => {
  const status = STATUS_LABEL[release.status] || STATUS_LABEL.draft;
  const isPublishing = publishing === release.release_id;
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 hover:border-[#CBD5E1] transition-colors" data-testid={`release-row-${release.release_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
            <Megaphone className="w-4 h-4 text-[#0052FF]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-[#020617]">{release.title}</h3>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${status.cls}`}>{status.label}</span>
            </div>
            <p className="text-sm text-[#64748B] mt-0.5 line-clamp-2">{release.summary}</p>
            <div className="flex items-center gap-3 flex-wrap mt-2 text-xs text-[#94A3B8]">
              <span>Audience: <strong className="text-[#475569]">{release.audience_label || release.audience}</strong></span>
              {release.linked_flag && (<span>· Flag: <code className="bg-[#F1F5F9] px-1 py-0.5 rounded">{release.linked_flag}</code></span>)}
              {release.status === 'published' && (
                <span>· Sent to {release.email_sent_count || 0}{release.email_failed_count ? ` (${release.email_failed_count} failed)` : ''}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {release.status === 'draft' && (
            <>
              <Button variant="outline" size="sm" onClick={() => onEdit(release)} data-testid={`release-edit-${release.release_id}`}>
                <Pencil className="w-3.5 h-3.5 mr-1" />Edit
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDelete(release)} className="text-[#991B1B] border-[#FECACA] hover:bg-[#FEE2E2]" data-testid={`release-delete-${release.release_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" onClick={() => onPublish(release)} disabled={isPublishing} className="bg-[#0052FF] hover:bg-[#003ECC] text-white" data-testid={`release-publish-${release.release_id}`}>
                <Send className="w-3.5 h-3.5 mr-1" />{isPublishing ? 'Publishing…' : 'Publish'}
              </Button>
            </>
          )}
          {release.status === 'published' && (
            <Button variant="outline" size="sm" onClick={() => onArchive(release)} data-testid={`release-archive-${release.release_id}`}>
              <Archive className="w-3.5 h-3.5 mr-1" />Archive
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReleases;
